import { NetworkStatus } from '@apollo/client';

import type { ObservableQuery, OperationVariables } from '@apollo/client';

/**
 * Variables that re-request everything a paginated query has loaded so far,
 * or `undefined` when its own variables already cover it.
 *
 * A Relay connection loaded page by page with `fetchMore` keeps the query's
 * original `first` (one page) and no `after`. Re-running the query with those
 * variables — a poll, a refetch — returns page 1 only, and
 * `relayStylePagination` then replaces every loaded page with it. On My Tasks
 * that dropped Unscheduled back to 50 rows and project groups back to 20
 * every 30 s; with the list scrolled to the end, onEndReached re-loaded the
 * rest above the viewport and the list jumped up about a screen (TA-11016).
 *
 * The loaded count is the longest `edges` array among the result's top-level
 * fields; `after`/`before`-scoped or `first`-less queries are left alone.
 */
export const variablesAtLoadedDepth = <
  TData,
  TVariables extends OperationVariables,
>(
  observableQuery: ObservableQuery<TData, TVariables>
): TVariables | undefined => {
  const variables = observableQuery.options.variables;
  if (
    !variables ||
    typeof variables.first !== 'number' ||
    variables.after != null ||
    variables.before != null
  ) {
    return undefined;
  }
  const data = observableQuery.getCurrentResult(false).data;
  if (!data || typeof data !== 'object') return undefined;
  let loaded = 0;
  Object.values(data as Record<string, unknown>).forEach((field) => {
    const edges = (field as { edges?: unknown } | null)?.edges;
    if (Array.isArray(edges)) loaded = Math.max(loaded, edges.length);
  });
  return loaded > variables.first ? { ...variables, first: loaded } : undefined;
};

/**
 * `ObservableQuery.refetch()` that keeps the pages `fetchMore` added: it
 * re-requests the loaded depth (variablesAtLoadedDepth) instead of page 1.
 * Used by My Tasks' refetch on focus, which otherwise dropped Unscheduled back
 * to 50 rows on every Task Details → back (TA-11217).
 *
 * A disposable network fetch like `refetch()`: the query's own variables stay
 * one page, so `fetchMore` keeps paging by its page size. With only page 1
 * loaded it is `refetch()` itself.
 */
export const refetchAtLoadedDepth = <
  TData,
  TVariables extends OperationVariables,
>(
  observableQuery: ObservableQuery<TData, TVariables>
): Promise<unknown> => {
  const variables = variablesAtLoadedDepth(observableQuery);
  if (!variables) return observableQuery.refetch();
  return observableQuery.reobserve(
    {
      fetchPolicy:
        observableQuery.options.fetchPolicy === 'no-cache'
          ? 'no-cache'
          : 'network-only',
      variables,
    },
    NetworkStatus.refetch
  );
};
